import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Trans, t } from '@lingui/macro';
import PropTypes from 'prop-types';

import { maybeMarkViewFacetAsToCompute } from 'actions/facet';
import { actionTypes, validateReviewClassifications } from 'actions/search';
import { createLoadingSelector } from 'reducers/ui';
import {
  conceptsItemsSelector,
  conceptsSelector,
  ontologiesByIdSelector,
} from 'selectors/entities';

import * as Sentry from '@sentry/react';

import {
  AnalyticsAwareButton,
  ButtonSuccess,
  ButtonTransparent,
} from 'components/ui/button/index';
import SidePane from 'components/ui/panels/SidePane';

import commonPropTypes from 'utils/commonPropTypes';
import capitalizedTranslation from 'utils/i18n';

import * as svars from 'assets/style/variables';

import ReviewValidationElement from './ReviewValidationElement';

const CLASSIFIED_ITEMS = [
  ['texts', 'items_texts', 'sentiment_score_texts'],
  ['positive_points', 'items_positive', 'sentiment_score_positive_points'],
  ['negative_points', 'items_negative', 'sentiment_score_negative_points'],
];

const reviewValidationLoadingSelector = createLoadingSelector([
  actionTypes.VALIDATE_REVIEW_CLASSIFICATIONS_REQUEST,
]);

const getConceptOptions = (
  classifications,
  classificationsField,
  index,
  conceptItems
) =>
  conceptItems.filter(
    (concept) =>
      !classifications ||
      !Object.values(classifications).some((localOntologyClassifications) =>
        localOntologyClassifications?.[classificationsField]?.[index]?.some(
          (classification) => classification.db_concept.id === concept.value
        )
      )
  );

function ReviewValidationPane({
  review,
  onClose,
  visible,
  highlightTextField,
  highlightChunkIndex,
}) {
  const dispatch = useDispatch();
  const [localSentiments, setLocalSentiments] = useState([]);
  const [localClassifications, setLocalClassifications] = useState({});
  const [nUpdated, setNUpdated] = useState(0);

  const conceptItems = useSelector(conceptsItemsSelector());
  const concepts = useSelector(conceptsSelector());
  const ontologiesById = useSelector(ontologiesByIdSelector);

  useEffect(() => {
    if (!visible) {
      // Reset local state when closing the pane
      setLocalSentiments([]);
      setLocalClassifications({});
      setNUpdated(0);
    } else {
      setLocalSentiments({
        sentiment_score_texts: review?.sentiment_score_texts || [],
        sentiment_score_positive_points:
          review?.sentiment_score_positive_points,
        sentiment_score_negative_points:
          review?.sentiment_score_negative_points,
      });
      setLocalClassifications(review?.classifications || {});
    }
  }, [review, visible]);

  useLayoutEffect(() => {
    if (visible && highlightTextField && highlightChunkIndex !== null) {
      const element = document.getElementById(
        `highlight-${highlightTextField}-${highlightChunkIndex}`
      );
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            block: 'end',
            inline: 'center',
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  }, [visible, highlightTextField, highlightChunkIndex]);

  const loading = useSelector(reviewValidationLoadingSelector);

  const onSaveClassifications = () => {
    dispatch(
      validateReviewClassifications(review.id, {
        classifications: localClassifications,
        sentiments: localSentiments,
      })
    )
      .then(() => {
        dispatch(maybeMarkViewFacetAsToCompute());
        onClose();
      })
      .catch((error) => {
        Sentry.captureException(
          `An error occurred while validating review classifications: ${error?.message}`
        );
      });
  };

  const onUpdateSentiment =
    (fieldName, chunkIndex) =>
    (event, { value }) => {
      const updatedSentiments = { ...localSentiments };
      updatedSentiments[fieldName] = [...(updatedSentiments[fieldName] || [])];
      updatedSentiments[fieldName][chunkIndex] = value;
      setLocalSentiments(updatedSentiments);
      setNUpdated(nUpdated + 1);
    };

  const onDeleteClassification = useCallback(
    (fieldName, textIndex) => (ontologyName) => (classificationId) => () => {
      const newClassifications = { ...localClassifications };
      newClassifications[ontologyName] = {
        ...newClassifications[ontologyName],
      };
      newClassifications[ontologyName][fieldName] = [
        ...(newClassifications[ontologyName][fieldName] || []),
      ];

      // FIX THIS Add field name to the object
      newClassifications[ontologyName][fieldName][textIndex] =
        newClassifications[ontologyName][fieldName][textIndex].filter(
          (c) =>
            c.db_concept.id !== classificationId &&
            !concepts[c.db_concept.id]?.parentIds?.includes(classificationId)
        );
      setLocalClassifications(newClassifications);
      setNUpdated(nUpdated + 1);
    },
    [localClassifications, nUpdated]
  );

  const onAddClassification = useCallback(
    (fieldName, textIndex) =>
      (event, { value }) => {
        // const ontologyName = ontologiesById?.find(
        //   (ontology) => ontology.value === concepts[value]?.ontologyId
        // )?.name;
        const ontologyName =
          ontologiesById?.[concepts[value]?.ontologyId]?.name;
        const updatedClassifications = [
          ...(localClassifications[ontologyName]?.[fieldName]?.length
            ? localClassifications[ontologyName][fieldName]
            : Array.from({
                length:
                  review[
                    CLASSIFIED_ITEMS.find((item) => item[1] === fieldName)[0]
                  ].length,
              }).map(() => [])),
        ];

        const addClassification = (id) => {
          const newClass = {
            db_concept: { id },
            probability: 1,
            validated: true,
          };
          const isAlreadyPresent = updatedClassifications[textIndex]?.some(
            (classification) => classification.db_concept.id === id
          );
          if (!isAlreadyPresent) {
            updatedClassifications[textIndex] = [
              ...(updatedClassifications[textIndex] || []),
              newClass,
            ];
          }
        };

        addClassification(value);

        const parentIds = concepts[value]?.parentIds || [];
        parentIds.forEach((parentId) => {
          addClassification(parentId);
        });

        setLocalClassifications({
          ...localClassifications,
          [ontologyName]: {
            ...(localClassifications[ontologyName] || {}),
            [fieldName]: updatedClassifications,
          },
        });
        setNUpdated(nUpdated + 1);
      },
    [concepts, localClassifications, nUpdated, ontologiesById, review]
  );

  if (!review) {
    return null;
  }

  return (
    <SidePane
      dimmed
      locked
      dimmerColor={svars.colorBlack}
      onToggle={onClose}
      animation="push"
      direction="right"
      visible={visible}
      width="very wide"
    >
      <SidePane.Header
        title={t({ id: 'correct-classifications' })}
        onToggle={onClose}
        gaCategory="Dashboard - search - validation panel"
      />
      <SidePane.Body style={{ marginTop: svars.spaceNormalLarge }}>
        {CLASSIFIED_ITEMS.map(
          ([textsField, classificationsField, sentimentField]) =>
            review?.[textsField]?.map((text, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                <ReviewValidationElement
                  id={`highlight-${textsField}-${index}`}
                  text={text}
                  index={index}
                  onAddClassification={onAddClassification(
                    classificationsField,
                    index
                  )}
                  onUpdateSentiment={onUpdateSentiment(sentimentField, index)}
                  onDeleteClassification={onDeleteClassification(
                    classificationsField,
                    index
                  )}
                  sentiment={localSentiments?.[sentimentField]?.[index] || null}
                  classifications={localClassifications}
                  classificationsField={classificationsField}
                  conceptOptions={getConceptOptions(
                    localClassifications,
                    classificationsField,
                    index,
                    conceptItems
                  )}
                  style={{
                    background:
                      highlightTextField === textsField &&
                      highlightChunkIndex === index
                        ? svars.textHighlightGradient
                        : 'transparent',
                  }}
                />
              </React.Fragment>
            ))
        )}
      </SidePane.Body>
      <SidePane.Actions visible>
        <AnalyticsAwareButton
          gaCategory="Campaign - search - validation"
          gaAction="Cancel validation"
          inputComponent={ButtonTransparent}
          labelPosition="right"
          content={<Trans render={capitalizedTranslation} id="cancel" />}
          onClick={onClose}
          disabled={loading}
          style={{ marginRight: svars.spaceMediumLarge }}
          nowrap="true"
        />
        <AnalyticsAwareButton
          gaCategory="Campaign - search"
          gaAction="Apply filters"
          inputComponent={ButtonSuccess}
          labelPosition="right"
          content={t({ id: 'save-classifications' })}
          onClick={!loading ? onSaveClassifications : null}
          icon="save"
          loading={loading}
          disabled={!nUpdated || loading}
          nowrap="true"
        />
      </SidePane.Actions>
    </SidePane>
  );
}

ReviewValidationPane.propTypes = {
  review: commonPropTypes.review,
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  highlightTextField: PropTypes.string,
  highlightChunkIndex: PropTypes.number,
};

ReviewValidationPane.defaultProps = {
  review: null,
  highlightTextField: null,
  highlightChunkIndex: null,
};

export default ReviewValidationPane;
